import React, { useCallback, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { type AutocompleteRenderValueGetItemProps, Form } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { generateEmailSchema } from "Utils";
import { EditableChip } from "./EditableChip";
import styles from "./emailInput.module.scss";
import {
  hasDelimiters,
  shouldFlushEmailForEventKey,
  shouldUndoForEventKey,
  splitEmails,
} from "./utils";

type EmailInputProps = {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  emailSuggestions?: string[];
  required?: boolean;
  className?: string;
  id?: string;
  autofocus?: boolean;
  onPendingInputChange?: (value: string) => void;
};

const EmailInput = ({
  name,
  label = "Emails",
  placeholder = "Enter an email, then press Enter, Space or comma to add it.",
  helperText = "Enter email addresses",
  emailSuggestions = [],
  required = false,
  className,
  id,
  autofocus = true,
  onPendingInputChange,
}: EmailInputProps) => {
  const {
    formState: { errors },
    control,
  } = useFormContext();
  const [inputValue, setInputValue] = useState("");

  const { field } = useController({ name, control });

  const updatePendingState = useCallback(
    (newVal: string) => {
      setInputValue(newVal);
      onPendingInputChange?.(newVal);
    },
    [onPendingInputChange],
  );

  const addEmailsToField = useCallback(
    (emailsToAdd: string[]) => {
      const currentValues: string[] = field.value || [];

      const newEmails = emailsToAdd
        .map((email) => email.trim())
        .filter((email) => email !== "");

      if (newEmails.length > 0) {
        field.onChange([...currentValues, ...newEmails]);
      }
    },
    [field],
  );

  const flushEmails = useCallback(
    (textToFlush: string) => {
      if (!textToFlush.trim()) return;

      addEmailsToField(splitEmails(textToFlush));
      updatePendingState("");
    },
    [addEmailsToField, updatePendingState],
  );

  // Flush pending input to RHF state on blur to capture last email input, if any
  const handleBlur = useCallback(() => {
    field.onBlur();
    flushEmails(inputValue);
  }, [field, inputValue, flushEmails]);

  const emailSchema = generateEmailSchema();

  const handleInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string,
  ) => {
    if (reason === "input") {
      if (hasDelimiters(value)) {
        flushEmails(value);
      } else {
        updatePendingState(value);
      }
    } else if (reason === "clear" || reason === "reset") {
      updatePendingState("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (shouldFlushEmailForEventKey(event.key)) {
      event.preventDefault();
      flushEmails(inputValue);
    } else if (shouldUndoForEventKey(event.key)) {
      event.preventDefault();
      updatePendingState("");
    }
  };

  // the autocomplete React (classic) ref does not work, and we need to force the focus
  const forceFocusOnAutocomplete = () => {
    const autocomplete = document.getElementById(id || name);
    if (autocomplete) {
      autocomplete.focus();
    }
  };

  const renderValue = (
    value: string[],
    getItemProps: AutocompleteRenderValueGetItemProps<true>,
  ) => {
    return value.map((option, index) => {
      const isInvalid = !emailSchema.isValidSync(option);
      const {
        key,
        className,
        onDelete: handleDelete,
      } = getItemProps({ index });

      // this is similar to flushEmails, but it preserves the order of emails (after edit saving)
      const handleEdit = (newVal: string) => {
        const updated = [...field.value];
        updated[index] = newVal;
        field.onChange(updated);
        forceFocusOnAutocomplete();
      };

      return (
        <EditableChip
          key={key}
          label={option}
          className={getClassName(className, {
            [styles.error]: isInvalid,
          })}
          onEdit={handleEdit}
          onDelete={() => {
            handleDelete("");
          }}
        />
      );
    });
  };

  return (
    <Form.Autocomplete
      label={label}
      name={name}
      id={id || name}
      freeSolo
      multiSelect
      multiline
      options={emailSuggestions}
      placeholder={field.value?.length > 0 ? "" : placeholder}
      helperText={helperText}
      isRequired={required}
      className={getClassName(styles.root, className)}
      errors={errors}
      getOptionLabel={(option: string | { label: string }) =>
        typeof option === "string" ? option : option.label || ""
      }
      value={field.value || []}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      {...(autofocus && { autoFocus: true })}
      renderValue={
        renderValue as (
          value: unknown[],
          getItemProps: AutocompleteRenderValueGetItemProps<true>,
        ) => React.ReactNode
      }
    />
  );
};

EmailInput.displayName = "EmailInput";

export default EmailInput;
